import { Routes } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { crudGuard, detailGuard } from '~ngx-shared/guards';
import { breadcrumbResolver, crudResolver } from '~ngx-shared/resolvers';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./components/layout/layout.component').then(m => m.LayoutComponent),
    providers: [
      provideTranslocoScope('shared'),
      provideTranslocoScope('karima'),
      provideTranslocoScope('finance')
    ],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      /******************
       * HOME
       ******************/
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard-page/dashboard-page.component').then(
            m => m.DashboardPageComponent
          ),
        data: {
          breadcrumb: 'dashboard'
        }
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile-page/profile-page.component').then(m => m.ProfilePageComponent),
        data: {
          breadcrumb: 'profile'
        }
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings-page/settings-page.component').then(
            m => m.SettingsPageComponent
          ),
        data: {
          breadcrumb: 'settings'
        }
      },
      /******************
       * FINANCE
       ******************/
      {
        path: 'account-plan',
        data: {
          breadcrumb: 'finance.account_plan'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/account-plan-overview-page/account-plan-overview-page.component').then(
                m => m.AccountPlanOverviewPageComponent
              )
          },
          {
            path: 'edit',
            loadComponent: () =>
              import('./pages/account-plan-page/account-plan-page.component').then(
                m => m.AccountPlanPageComponent
              ),
            data: {
              breadcrumb: 'edit_value',
              breadcrumbParamValue: 'finance.account_plan',
              icon: 'pi pi-fw pi-money-bill'
            }
          },
          {
            path: 'account-groups',
            data: {
              breadcrumb: 'finance.account_groups',
              icon: 'pi pi-fw pi-money-bill'
            },
            children: [
              {
                path: '',
                redirectTo: '/account-plan',
                pathMatch: 'full'
              },
              {
                path: 'detail/:account-group-id',
                loadComponent: () =>
                  import('./pages/account-group-detail-page/account-group-detail-page.component').then(
                    m => m.AccountGroupDetailPageComponent
                  ),
                canActivate: [detailGuard('account-group-id')],
                resolve: { crud: crudResolver('account-group-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'finance.account_group'
                }
              },
              {
                path: ':account-group-id',
                loadComponent: () =>
                  import('./pages/create-update-account-group-form-page/create-update-account-group-form-page.component').then(
                    m => m.CreateUpdateAccountGroupFormPageComponent
                  ),
                canActivate: [crudGuard('account-group-id')],
                resolve: {
                  crud: crudResolver('account-group-id'),
                  breadcrumb: breadcrumbResolver('account-group-id')
                },
                data: {
                  breadcrumbParamValue: 'finance.account_group'
                }
              }
            ]
          },
          {
            path: 'accounts',
            data: {
              breadcrumb: 'finance.account',
              icon: 'pi pi-fw pi-money-bill'
            },
            children: [
              {
                path: '',
                redirectTo: '/account-plan',
                pathMatch: 'full'
              },
              {
                path: 'detail/:account-id',
                loadComponent: () =>
                  import('./pages/account-detail-page/account-detail-page.component').then(
                    m => m.AccountDetailPageComponent
                  ),
                canActivate: [detailGuard('account-id')],
                resolve: { crud: crudResolver('account-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'finance.account'
                }
              },
              {
                path: ':account-id',
                loadComponent: () =>
                  import('./pages/create-update-account-form-page/create-update-account-form-page.component').then(
                    m => m.CreateUpdateAccountFormPageComponent
                  ),
                canActivate: [crudGuard('account-id')],
                resolve: {
                  crud: crudResolver('account-id'),
                  breadcrumb: breadcrumbResolver('account-id')
                },
                data: {
                  breadcrumbParamValue: 'finance.account'
                }
              }
            ]
          }
        ]
      },
      {
        path: 'entries',
        data: {
          breadcrumb: 'finance.entries'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/entry-list-page/entry-list-page.component').then(
                m => m.EntryListPageComponent
              )
          },
          {
            path: 'detail/:entry-number',
            loadComponent: () =>
              import('./pages/entry-detail-page/entry-detail-page.component').then(
                m => m.EntryDetailPageComponent
              ),
            canActivate: [detailGuard('entry-number')],
            resolve: { crud: crudResolver('entry-number', 'number') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'finance.entry'
            }
          },
          {
            path: 'detail/id/:entry-id',
            loadComponent: () =>
              import('./pages/entry-detail-page/entry-detail-page.component').then(
                m => m.EntryDetailPageComponent
              ),
            canActivate: [detailGuard('entry-id')],
            resolve: { crud: crudResolver('entry-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'finance.entry'
            }
          },
          {
            path: ':entry-id',
            loadComponent: () =>
              import('./pages/create-entry-form-page/create-entry-form-page.component').then(
                m => m.CreateEntryFormPageComponent
              ),
            canActivate: [crudGuard('entry-id', true, false)],
            resolve: {
              crud: crudResolver('entry-id'),
              breadcrumb: breadcrumbResolver('entry-id')
            },
            data: {
              breadcrumbParamValue: 'finance.entry'
            }
          }
        ]
      },
      {
        path: 'camt-import',
        data: {
          breadcrumb: 'finance.camt_import'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/camt-import-list-page/camt-import-list-page.component').then(
                m => m.CamtImportListPageComponent
              )
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./pages/create-camt-import-form-page/create-camt-import-form-page.component').then(
                m => m.CreateCamtImportFormPageComponent
              ),
            data: {
              breadcrumb: 'add_value',
              breadcrumbParamValue: 'finance.camt_import'
            }
          },
          {
            path: ':camt-import-id',
            loadComponent: () =>
              import('./pages/camt-import-page/camt-import-page.component').then(
                m => m.CamtImportPageComponent
              )
          }
        ]
      },
      {
        path: 'period',
        data: {
          breadcrumb: 'finance.accounting_period'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/period-page/period-page.component').then(m => m.PeriodPageComponent)
          }
        ]
      },
      {
        path: 'periods',
        data: {
          breadcrumb: 'finance.accounting_periods'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/period-list-page/period-list-page.component').then(
                m => m.PeriodListPageComponent
              )
          },
          {
            path: 'finalize',
            data: {
              breadcrumb: 'shared.finalize'
            },
            loadComponent: () =>
              import('./pages/finalize-period-page/finalize-period-page.component').then(
                m => m.FinalizePeriodPageComponent
              )
          }
        ]
      },
      /******************
       * ACTORS
       ******************/
      // {
      //   path: 'actors',
      //   data: {
      //     breadcrumb: 'shared.actors'
      //   },
      //   children: [
      //     {
      //       path: 'people',
      //       data: {
      //         breadcrumb: 'shared.people'
      //       },
      //       children: [
      //         {
      //           path: '',
      //           loadComponent: () =>
      //             import('./pages/person-list-page/person-list-page.component').then(
      //               m => m.PersonListPageComponent
      //             )
      //         },
      //         {
      //           path: 'detail/:person-id',
      //           loadComponent: () =>
      //             import('./pages/person-detail-page/person-detail-page.component').then(
      //               m => m.PersonDetailPageComponent
      //             ),
      //           canActivate: [detailGuard('person-id')],
      //           resolve: { crud: crudResolver('person-id') },
      //           data: {
      //             breadcrumb: 'detail_value',
      //             breadcrumbParamValue: 'shared.person'
      //           }
      //         },
      //       ]
      //     },
      //   ]
      // },
      /******************
       * NOT FOUND
       ******************/
      {
        path: 'not-found',
        loadComponent: () =>
          import('~shared/shared/pages/page-not-found-page/page-not-found-page.component').then(
            m => m.PageNotFoundPageComponent
          ),
        data: {
          breadcrumb: 'page_not_found'
        }
      },
      { path: '**', redirectTo: 'not-found' }
    ]
  }
];
